import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heart, ShieldCheck, Landmark } from "lucide-react";
import RichHtml from "@/components/RichHtml";

export default function Giving({ content }) {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/donations/packages").then((r) => setPackages(r.data || [])).catch(() => {});
  }, []);

  const give = async (packageId) => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout — please try again or use a local giving method below.");
      setLoading(false);
    }
  };

  return (
    <section id="giving" data-testid="giving-section" className="py-24 lg:py-32 bg-[#FAF9F6]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.2em] text-[#5C6661] mb-4">Cheerful giving</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#1C201E] leading-tight">
              {content?.heading || "Give & Support the Ministry"}
            </h2>
            <p className="mt-4 text-[#5C6661]">{content?.subtitle}</p>
            <div className="mt-6 text-[#1C201E]/85 leading-relaxed">
              <RichHtml html={content?.intro} testId="giving-intro" />
            </div>
            {content?.verse && (
              <blockquote className="mt-6 pl-5 border-l-2 border-[#D4A373] font-serif-display text-lg text-[#2C4C3B] italic leading-snug">
                {content.verse}
              </blockquote>
            )}
          </div>

          <div className="lg:col-span-7">
            <div className="p-8 bg-white border border-[#E5E0D8] rounded-3xl shadow-[0_20px_60px_rgba(0,0,0,0.04)]">
              <div className="flex items-center gap-2 mb-4 text-[#2C4C3B]">
                <ShieldCheck className="w-4 h-4" />
                <span className="text-xs uppercase tracking-[0.2em]">Secure Card Giving</span>
              </div>
              <h3 className="font-serif-display text-2xl text-[#1C201E]">Give online</h3>
              <p className="mt-2 text-sm text-[#5C6661]">Powered by Stripe. Test card 4242 4242 4242 4242, any future expiry, any CVC.</p>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                {packages.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`give-btn-${p.id}`}
                    disabled={loading}
                    onClick={() => give(p.id)}
                    className="p-4 rounded-2xl border border-[#E5E0D8] hover:border-[#2C4C3B] hover:-translate-y-0.5 transition-all bg-[#FAF9F6] disabled:opacity-60"
                  >
                    <div className="text-xs uppercase tracking-[0.15em] text-[#5C6661]">USD</div>
                    <div className="mt-1 font-serif-display text-3xl text-[#1C201E]">${p.amount}</div>
                    <div className="mt-2 inline-flex items-center gap-1 text-xs text-[#2C4C3B]">
                      <Heart className="w-3 h-3" /> Give
                    </div>
                  </button>
                ))}
              </div>
              {loading && <div data-testid="giving-loading" className="mt-3 text-xs text-[#5C6661]">Redirecting to secure checkout…</div>}

              <div className="mt-10">
                <div className="flex items-center gap-2 mb-4 text-[#2C4C3B]">
                  <Landmark className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-[0.2em]">Local Giving</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {(content?.methods || []).map((m, i) => (
                    <div key={i} data-testid={`giving-method-${i}`} className="p-4 rounded-xl border border-[#E5E0D8] bg-[#FAF9F6]">
                      <div className="text-sm font-medium text-[#1C201E]">{m.name}</div>
                      <div className="mt-1 text-sm text-[#5C6661] whitespace-pre-line">{m.details}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
